import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import PrimeReact from 'primereact/api'
import './index.css'
import './styles/primereact-overrides.css'
import Router from './router.js'
import SingleTabGuard from './components/SingleTabGuard.js'
import { useAuthStore } from './store/authStore.js'
import { endSessionNow, hasAliveCookie, syncAliveCookie } from './auth/sessionLifecycle.js'

/**
 * Lift PrimeReact's overlay layers above `ResourceDrawer`.
 *
 * The drawer is a plain Tailwind overlay at z-9999 (backdrop 9998), while
 * PrimeReact's default modal base is 1100 — so any `Dialog` opened FROM inside
 * a drawer rendered behind it and looked like nothing had happened. Setting the
 * base here fixes it once for every dialog in the app rather than each one
 * passing its own `baseZIndex`.
 *
 * Every overlay PANEL (dropdown, multiselect, calendar, …) is pinned higher
 * still — 100000, in the OVERLAY LAYER SCALE block at the top of
 * styles/primereact-overrides.css — so a picker opened inside such a dialog
 * stays on top of it. That file documents the full scale; keep these two
 * numbers consistent with it.

*
 * `toast` MUST be listed here too. Assigning a fresh object replaces
 * PrimeReact's defaults wholesale, so omitting a key leaves it `undefined` —
 * and Toast writes that base as an INLINE z-index on its root each time a
 * message shows, which overrides the class on the <Toast> element. Every
 * toast fired from inside a drawer or dialog (e.g. "User created
 * successfully") therefore rendered underneath it and looked like nothing
 * happened. Toasts are the topmost layer: nothing may cover them.
 */
PrimeReact.zIndex = {
  modal: 10010,
  overlay: 10010,
  menu: 10010,
  tooltip: 10020,
  toast: 10030,
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
    },
  },
})

/**
 * A page reload ends the session — the agent must sign in again rather than
 * silently re-entering the app. Detected via the navigation-timing entry
 * ('reload' covers F5/Ctrl+R/reload button, not first navigation or bfcache
 * restores). Runs before render so the auth guard never sees a live session:
 * the logout POST (accepted with an expired token, see routes/auth/logout.ts)
 * clears the kc3.* cookies and the refresh family; the sessionStorage marker
 * then tells requireAuth to skip its /auth/me re-entry fallback — so even if
 * the request fails, the reload still lands on the login screen.
 * The same applies to the first load after the browser was closed.
 */
async function logoutOnReload(): Promise<void> {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
  const isReload =
    nav?.type === 'reload' ||
    // Deprecated navigation API — fallback for browsers without L2 entries.
    (performance as unknown as { navigation?: { type?: number } }).navigation?.type === 1
  // The kc3.alive session cookie is gone although a user is persisted: the
  // browser was closed since sign-in (see auth/sessionLifecycle.ts).
  const browserRestarted = !hasAliveCookie()
  if (!isReload && !browserRestarted) return
  if (!useAuthStore.getState().user) return

  // Best effort — endSessionNow's marker keeps the guard from re-entering
  // even when the server-side cleanup didn't land.
  await Promise.race([endSessionNow(false), new Promise((r) => setTimeout(r, 3000))])
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element not found')

void logoutOnReload().finally(() => {
  syncAliveCookie()
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <SingleTabGuard>
          <Router />
        </SingleTabGuard>
      </QueryClientProvider>
    </StrictMode>,
  )
})
